import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { useNavigate } from 'react-router-dom';
import FamilySidebar from '../family/FamilySidebar.tsx';
import FamilyProfile from '../family/FamilyProfile.tsx';
import NotificationsList from '../family/NotificationsList.tsx';
import MonthlyDuesModal from '../family/MonthlyDuesModal.tsx';
import EventCalendar from '../family/EventCalendar.tsx';
import DocumentsView from '../family/DocumentsView.tsx';
import HorsesView from '../family/HorsesView.tsx';
import DirectoryView from '../family/DirectoryView.tsx';
import GirlsCornerView from '../family/GirlsCornerView.tsx';
import { AppIcon } from '../../icons/appIcons.tsx';

// Helper function to format date string without timezone conversion
const formatDateSafe = (dateStr: string) => {
  const [year, month, day] = dateStr.split('-');
  return `${month}/${day}/${year}`;
};

interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  maxAttendees: number;
  currentAttendees: number;
  rsvpEnabled: boolean;
  rsvpDeadline?: string;
  createdBy: string;
  createdAt: string;
  rsvps: any[];
}

interface RanchGirl {
  id: string;
  firstName: string;
  lastName: string;
  age: number;
  dateOfBirth: string;
  gradeLevel: string;
  skillLevel: string;
  horseAssigned: string;
  assignedHorse?: {
    id: string;
    name: string;
    breed: string;
    age: number;
    color: string;
    imageUrl: string;
  };
  memberSince: string;
  graduationYear: number;
  isActive: boolean;
}

interface FamilyData {
  id: string;
  familyName: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  secondaryContactFirstName: string;
  secondaryContactLastName: string;
  secondaryContactEmail: string;
  secondaryContactPhone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  membershipStatus: string;
  monthlyFee: number;
  joinDate: string;
  ranchGirls: RanchGirl[];
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('dashboard');
  const [familyData, setFamilyData] = useState<FamilyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [showDuesModal, setShowDuesModal] = useState(false);

  useEffect(() => {
    fetchFamilyData();
    setTimeout(() => {
      fetchUpcomingEvents();
    }, 500);
  }, []);

  const fetchFamilyData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/families/profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 429) {
        console.warn('Rate limited - delaying family data fetch');
        setLoading(false);
        return;
      }

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Failed to parse family data response:', parseError);
        setLoading(false);
        return;
      }

      if (data.success) {
        setFamilyData(data.data);
      }
    } catch (error) {
      console.error('Error fetching family data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingEvents = async () => {
    try {
      setEventsLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/events?upcoming=true', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 429) {
        console.warn('Rate limited - delaying events fetch');
        setEventsLoading(false);
        return;
      }

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Failed to parse events response:', parseError);
        setEventsLoading(false);
        return;
      }

      if (data.success) {
        setUpcomingEvents(data.data);
      }
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
    } finally {
      setEventsLoading(false);
    }
  };

  const handleNavigate = (view: string) => {
    if (view === 'monthly-dues') {
      setShowDuesModal(true);
      return;
    }
    setActiveView(view);
  };

  // Function to check if family has RSVP'd for an event
  const getFamilyRSVP = (event: Event) => {
    if (!user?.familyId) return null;
    return event.rsvps.find(rsvp => rsvp.familyId === user.familyId);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderContent = () => {
    switch (activeView) {
      case 'events':
        return <EventCalendar />;
      case 'horses':
        return <HorsesView />;
      case 'profile':
        return <FamilyProfile />;
      case 'documents':
        return <DocumentsView />;
      case 'directory':
        return <DirectoryView />;
      case 'girlscorner':
        return <GirlsCornerView />;
      case 'notifications':
        return <NotificationsList />;
      default:
        // Dashboard home view
        return (
          <div className="px-4 sm:px-6 lg:px-12 py-4 sm:py-6 lg:py-10">
            {/* Welcome Header */}
            <div className="mb-6 sm:mb-8">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Welcome, {familyData?.familyName} Family!</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Here's what's happening at the ranch</p>
            </div>

            {/* Monthly dues & notifications */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl shadow-lg border-2 border-blue-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-4 sm:px-6 py-3">
                  <div className="flex items-center">
                    <AppIcon name="monthlyDues" className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 shrink-0" />
                    <h2 className="text-base sm:text-lg font-semibold">Monthly Dues</h2>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm sm:text-base text-gray-700 mb-1">
                        Your monthly dues:
                      </p>
                      <p className="text-2xl sm:text-3xl font-bold text-blue-700 mb-3">
                        ${(familyData?.monthlyFee ?? 0).toFixed(2)}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600 mb-4">
                        Due at the beginning of each month. Use the drop box in the shelter.
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowDuesModal(true)}
                        className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold py-2.5 sm:py-3 rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all shadow-md text-sm sm:text-base"
                      >
                        Payment Information
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Notifications Banner */}
              <div className="bg-gradient-to-r from-ggaa-green/5 to-ggaa-cream/20 rounded-xl shadow-lg border-2 border-ggaa-green/30 overflow-hidden">
                <div className="bg-ggaa-green text-white px-4 sm:px-6 py-3">
                  <div className="flex items-center">
                    <AppIcon name="notifications" className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 shrink-0" />
                    <h2 className="text-base sm:text-lg font-semibold">Ranch Notifications</h2>
                    <button
                      onClick={() => setActiveView('notifications')}
                      className="ml-auto text-xs sm:text-sm hover:underline"
                    >
                      View All →
                    </button>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  <NotificationsList 
                    compact={true} 
                    maxItems={3}
                    onViewAll={() => setActiveView('notifications')}
                  />
                </div>
              </div>
            </div>

            {/* Girls' Corner Section */}
            <div className="mb-6 sm:mb-8">
              <GirlsCornerView compact={true} />
            </div>

            {/* Main Dashboard Content - Three Cards in a Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {/* Ranch Girls */}
              <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-ggaa-green/20">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                  <AppIcon name="girlsCorner" className="w-5 h-5 text-ggaa-green" />
                  Our Ranch Girls
                </h3>
                {familyData && familyData.ranchGirls && familyData.ranchGirls.length > 0 ? (
                  <div className="space-y-3 sm:space-y-4">
                    {familyData.ranchGirls.map(girl => (
                      <div key={girl.id} className="bg-gradient-to-br from-ggaa-cream to-ggaa-light rounded-lg p-3 sm:p-4 border border-ggaa-green/10 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0 pr-2">
                            <h4 className="font-bold text-sm sm:text-base text-ggaa-brown truncate">{girl.firstName} {girl.lastName}</h4>
                            <p className="text-xs text-gray-600 mt-0.5">
                              {girl.age} years old • {girl.gradeLevel}
                            </p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${
                            girl.isActive 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {girl.isActive ? '✓ Active' : 'Inactive'}
                          </span>
                        </div>
                        {girl.assignedHorse ? (
                          <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-ggaa-green/20">
                            <div className="flex items-center">
                              <AppIcon name="horse" className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0 text-ggaa-green" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-ggaa-green truncate">{girl.assignedHorse.name}</p>
                                <p className="text-xs text-gray-500 truncate">
                                  {girl.assignedHorse.breed} • {girl.assignedHorse.color}
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200">
                            <p className="text-xs text-gray-400 italic">No horse assigned yet</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 sm:py-12 text-gray-500">
                    <div className="text-3xl sm:text-4xl mb-2">👧</div>
                    <p className="text-xs sm:text-sm">No ranch girls registered</p>
                  </div>
                )}
              </div>

              {/* Upcoming Events */}
              <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-ggaa-green/20">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Upcoming Events</h3>
                  <button
                    onClick={() => setActiveView('events')}
                    className="text-xs sm:text-sm text-ggaa-green hover:underline"
                  >
                    View All
                  </button>
                </div>
                {eventsLoading ? (
                  <div className="flex items-center justify-center py-6 sm:py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ggaa-green"></div>
                    <span className="ml-2 text-sm text-gray-600">Loading...</span>
                  </div>
                ) : upcomingEvents.length === 0 ? (
                  <div className="text-center py-6 sm:py-8 text-gray-500">
                    <p className="text-xs sm:text-sm">No upcoming events</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {upcomingEvents.slice(0, 2).map((event, index) => {
                      const familyRSVP = getFamilyRSVP(event);
                      return (
                        <div key={event.id} className={`p-3 rounded-lg ${index === 0 ? 'bg-ggaa-cream' : 'bg-gray-50'}`}>
                          <p className={`font-medium text-sm ${index === 0 ? 'text-ggaa-brown' : 'text-gray-700'} mb-1`}>
                            {event.title}
                          </p>
                          <p className="text-xs text-gray-600">
                            {formatDateSafe(event.date)} at {event.time}
                          </p>
                          {familyRSVP && (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full font-medium">
                              ✓ RSVP'd
                            </span>
                          )}
                        </div>
                      );
                    })}
                    {upcomingEvents.length > 2 && (
                      <button
                        onClick={() => setActiveView('events')}
                        className="w-full text-center py-2 text-ggaa-green hover:underline text-xs sm:text-sm"
                      >
                        +{upcomingEvents.length - 2} more events
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Documents Preview */}
              <DocumentsView compact={true} />
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <FamilySidebar 
        currentView={showDuesModal ? 'monthly-dues' : activeView}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        familyName={familyData?.familyName}
      />
      <div className="flex-1 overflow-y-auto pt-16 lg:pt-0">
        {renderContent()}
      </div>
      {showDuesModal && (
        <MonthlyDuesModal
          monthlyFee={familyData?.monthlyFee ?? 0}
          onClose={() => setShowDuesModal(false)}
        />
      )}
    </div>
  );
} 